// App.js - Application principale avec navigation et authentification

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LangueInterfaceProvider } from './contexts/LangueInterfaceContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import './i18n/config';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Navigation';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MotDePasseOubliePage from './pages/MotDePasseOubliePage';
import NouveauMotDePassePage from './pages/NouveauMotDePassePage';
import ConfirmerInscriptionPage from './pages/ConfirmerInscriptionPage';
import HomePage from './pages/HomePage';
import AnalysePageV2 from './pages/AnalysePageV2';
import EnrichmentPage from './pages/EnrichmentPage';
import ConjugationExercise from './pages/ConjugationExercise';
import ConjugationCompleteExercise from './pages/ConjugationCompleteExercise';
import VocabularyExercise from './pages/VocabularyExercise';
import MatchingExercise from './pages/MatchingExercise';
import ChangerNiveau from './pages/ChangerNiveau';
import ConstructionPage from './pages/ConstructionPage';
import MotsSignales from './pages/MotsSignales';
import QuestionsSignalees from './pages/QuestionsSignalees';
import GestionDesMots from './pages/GestionDesMots';
import TraductionsIncompletes from './pages/TraductionsIncompletes';
import MotsIncomplets from './pages/MotsIncomplets';
import PlaylistsPage from './pages/PlaylistsPage';
import MotsPendingPage from './pages/MotsPendingPage';
import EnrichissementCompletPage from './pages/EnrichissementCompletPage';
import DashboardPage from './pages/DashboardPage';
import GrammarQuizExercise from './pages/GrammarQuizExercise';
import ThemesGeneriquesPage from './pages/ThemesGeneriquesPage';
import './App.css';
import { VERSION } from './version';
import ExportPage from './pages/ExportPage';
import ImportPage from './pages/ImportPage';
import PresentationPage from './pages/PresentationPage';
import TarifsPage from './pages/TarifsPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import ConditionsGeneralesPage from './pages/ConditionsGeneralesPage';
import ContactPage from './pages/ContactPage';
import ProfilePage from './pages/ProfilePage';
import CoherenceVerbesPage from './pages/CoherenceVerbesPage';

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <LangueInterfaceProvider>
          <AnalyticsProvider>
            <AppContent />
          </AnalyticsProvider>
        </LangueInterfaceProvider>
      </AuthProvider>
    </Router>
  );
}

// Composant qui gère l'accès aux exercices : ProtectedRoute en mode normal, accès libre en mode embed
function EmbedRoute({ element }) {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  return isEmbed ? element : <ProtectedRoute>{element}</ProtectedRoute>;
}

// Composant interne qui gère les layouts (normal vs embed)
function AppContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true' || location.pathname.includes('/embed/');
  const { loginWithWpToken, isAuthenticated, loading } = useAuth();

  // En mode embed : si un token WP est présent dans l'URL, ouvrir la session automatiquement
  useEffect(() => {
    const wpToken = searchParams.get('wpToken');
    if (isEmbed && wpToken && !isAuthenticated && !loading) {
      loginWithWpToken(wpToken).catch(() => {
        // Token invalide ou expiré : on reste en mode guest (userId=1)
      });
    }
  }, [isEmbed, isAuthenticated, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="app">
      <div className="container">
        {/* Navigation Menu - cachée si embed */}
        {!isEmbed && <Navigation />}

        {/* Routes - Pages */}
        <Routes>
              {/* Pages publiques (login/register) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/mot-de-passe-oublie" element={<MotDePasseOubliePage />} />
              <Route path="/nouveau-mot-de-passe" element={<NouveauMotDePassePage />} />
              <Route path="/confirmer-email" element={<ConfirmerInscriptionPage />} />
              <Route
                path="/presentation"
                element={
                  <ProtectedRoute>
                    <PresentationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tarifs"
                element={
                  <ProtectedRoute>
                    <TarifsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentions-legales"
                element={
                  <ProtectedRoute>
                    <MentionsLegalesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/conditions-generales"
                element={
                  <ProtectedRoute>
                    <ConditionsGeneralesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <ProtectedRoute>
                    <ContactPage />
                  </ProtectedRoute>
                }
              />
              {/* Pages protégées */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analyse"
                element={<EmbedRoute element={<AnalysePageV2 />} />}
              />
              <Route
                path="/enrichissement"
                element={
                  <ProtectedRoute>
                    <EnrichmentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exercice-conjugaison-partielle"
                element={<EmbedRoute element={<ConjugationExercise />} />}
              />
              <Route
                path="/exercice-conjugaison-complete"
                element={<EmbedRoute element={<ConjugationCompleteExercise />} />}
              />
              <Route
                path="/exercice-quiz-vocabulaire"
                element={<EmbedRoute element={<VocabularyExercise />} />}
              />
              <Route
                path="/exercice-matching"
                element={<EmbedRoute element={<MatchingExercise />} />}
              />
              <Route
                path="/changer-niveau"
                element={
                  <ProtectedRoute>
                    <ChangerNiveau />
                  </ProtectedRoute>
                }
              />

              {/* Pages de gestion */}
              <Route
                path="/gestion/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/mots"
                element={
                  <ProtectedRoute>
                    <GestionDesMots />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/traductions-incompletes"
                element={
                  <ProtectedRoute>
                    <TraductionsIncompletes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/mots-incomplets"
                element={
                  <ProtectedRoute>
                    <MotsIncomplets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/mots-signales"
                element={
                  <ProtectedRoute>
                    <MotsSignales />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/questions-signalees"
                element={
                  <ProtectedRoute>
                    <QuestionsSignalees />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/themes-generiques"
                element={
                  <ProtectedRoute>
                    <ThemesGeneriquesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/mots-pending"
                element={
                  <ProtectedRoute>
                    <MotsPendingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/enrichissement-complet"
                element={
                  <ProtectedRoute>
                    <EnrichissementCompletPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/gestion/coherence-verbes"
                element={
                  <ProtectedRoute>
                    <CoherenceVerbesPage />
                  </ProtectedRoute>
                }
              />

              {/* Playlists */}
              <Route
                path="/playlists"
                element={
                  <ProtectedRoute>
                    <PlaylistsPage />
                  </ProtectedRoute>
                }
              />

              {/* gestion/exports */}
              <Route
                path="/gestion/export"
                element={
                  <ProtectedRoute>
                    <ExportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestion/import"
                element={
                  <ProtectedRoute>
                    <ImportPage />
                  </ProtectedRoute>
                }
              />

              {/* Nouvelles pages - En construction */}
              <Route
                path="/exercice-quiz-grammaire"
                element={<EmbedRoute element={<GrammarQuizExercise />} />}
              />
              <Route
                path="/exercice-flash-cards"
                element={
                  <ProtectedRoute>
                    <ConstructionPage title={t('nav.flashCards')} description={t('construction.flashCardsDesc')} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profil"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistiques"
                element={
                  <ProtectedRoute>
                    <ConstructionPage title={t('nav.statistiques')} description={t('construction.statistiquesDesc')} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/abonnement"
                element={
                  <ProtectedRoute>
                    <ConstructionPage title={t('nav.abonnement')} description={t('construction.abonnementDesc')} />
                  </ProtectedRoute>
                }
              />
              {/* Routes EMBED - sans menu ni footer, sans authentification */}
              <Route
                path="/embed/exercice-conjugaison-complete"
                element={<ConjugationCompleteExercise />}
              />
            </Routes>
            {/* Version footer - cachée si embed */}
            {!isEmbed && (
              <footer className="app-version">
                Version {VERSION.number} - {VERSION.buildDate}
              </footer>
            )}
          </div>
        </div>
      );
    }
